import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { formatCurrency, formatDateTime } from "../lib/utils";
import USDHint from "../components/USDHint";
import PriceInput from "../components/PriceInput";
import { useCurrency } from "../contexts/CurrencyContext";
import toast from "react-hot-toast";
import axios from "axios";
import { Plus, Truck, Package, Trash2, Edit2, FileText, Printer, Camera, ScanLine } from "lucide-react";
import { ReceiptDialog } from "../components/receipts/ReceiptDialog";
import { BarcodeScanner } from "../components/BarcodeScanner";

const API = `${window.location.origin}/api`;

// فئات المشتريات
const purchaseCategories = [
  { value: "goods", labelAr: "بضاعة", labelEn: "Goods" },
  { value: "supplies", labelAr: "مستلزمات متجر", labelEn: "Store Supplies" },
  { value: "equipment", labelAr: "معدات", labelEn: "Equipment" },
  { value: "packaging", labelAr: "تغليف", labelEn: "Packaging" },
  { value: "other", labelAr: "أخرى", labelEn: "Other" },
];

export default function PurchasesPage() {
  const { } = useAuth();
  const { rate: usdRate } = useCurrency();
  const language = "ar";
  const { t } = useTranslation(language);
  const [purchases, setPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [inputMode, setInputMode] = useState("select"); // select أو manual
  const [receiptData, setReceiptData] = useState(null);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [itemQuantity, setItemQuantity] = useState("");
  const [itemPrice, setItemPrice] = useState("");
  const [manualProductName, setManualProductName] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [manualSupplierName, setManualSupplierName] = useState("");
  const [paymentType, setPaymentType] = useState("cash");
  const [amountPaid, setAmountPaid] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [purchaseCategory, setPurchaseCategory] = useState("goods");
  const [customCategory, setCustomCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [isScannerOpen, setIsScannerOpen] = useState(false); // ماسح الباركود
  const [scannerMode, setScannerMode] = useState(false); // وضع المسح USB المتواصل
  const [lastScanTime, setLastScanTime] = useState(0);
  // ✨ تاريخ مخصص للفاتورة
  const [useCustomDate, setUseCustomDate] = useState(false);
  const [customInvoiceDate, setCustomInvoiceDate] = useState("");
  const [editDatePurchase, setEditDatePurchase] = useState(null);
  const [editDateValue, setEditDateValue] = useState("");
  // ✨ تعديل بيانات الفاتورة (مستوى 1 - آمن)
  const [editMetaPurchase, setEditMetaPurchase] = useState(null);
  const [editMetaForm, setEditMetaForm] = useState({ supplier_name: "", notes: "", due_date: "" });
  // ✨ إصدار إشعار تصحيح (Debit Note)
  const [correctionPurchase, setCorrectionPurchase] = useState(null);
  const [correctionForm, setCorrectionForm] = useState({ direction: "decrease", amount: "", reason: "", adjust_debt: true });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [purchasesRes, productsRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/purchases`),
        axios.get(`${API}/products`),
        axios.get(`${API}/suppliers`),
      ]);
      setPurchases(purchasesRes.data);
      setProducts(productsRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error(t("error"));
    } finally {
      setLoading(false);
    }
  };

  // معالجة مسح الباركود للمشتريات
  const handlePurchaseBarcodeScan = (barcode) => {
    const now = Date.now();
    if (now - lastScanTime < 500) return; // منع المسح المتكرر
    setLastScanTime(now);
    
    // تشغيل صوت
    playBeepSound(true);
    
    const product = products.find(p => p.barcode === barcode || p.code === barcode);
    if (product) {
      setSelectedProduct(product.id);
      setItemPrice(product.purchase_price || product.cost_price || product.price || "");
      setInputMode("select");
      toast.success(
        language === "ar" 
          ? `✅ تم العثور على: ${product.name}` 
          : `✅ Found: ${product.name}`
      );
    } else {
      // منتج جديد - انتقل لوضع الإدخال اليدوي
      playBeepSound(false);
      setInputMode("manual");
      toast.info(
        language === "ar" 
          ? `📦 منتج جديد - باركود: ${barcode}` 
          : `📦 New product - Barcode: ${barcode}`
      );
    }
    setIsScannerOpen(false);
  };

  // 🔊 تشغيل صوت عند المسح
  const playBeepSound = (success = true) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = success ? 1200 : 300;
      oscillator.type = success ? 'sine' : 'square';
      gainNode.gain.value = success ? 0.3 : 0.2;
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);
    } catch (e) {
      console.log("Audio not supported");
    }
  };

  // الاستماع لماسح USB في وضع المسح المتواصل
  useEffect(() => {
    if (!scannerMode || !isDialogOpen) return;
    
    let buffer = "";
    let lastTime = Date.now();
    
    const handleKeyDown = (e) => {
      const currentTime = Date.now();
      const activeElement = document.activeElement;
      const isInputFocused = activeElement.tagName === "INPUT" || 
                            activeElement.tagName === "TEXTAREA";
      
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      
      // مسح المخزن المؤقت إذا مر وقت طويل
      if (currentTime - lastTime > 100) {
        buffer = "";
      }
      lastTime = currentTime;
      
      // عند Enter، معالجة الباركود
      if (e.key === "Enter" && buffer.length >= 3) {
        e.preventDefault();
        handlePurchaseBarcodeScan(buffer.trim());
        buffer = "";
        return;
      }
      
      // إضافة الأحرف للمخزن المؤقت
      if (e.key.length === 1 && /[a-zA-Z0-9\-_.]/.test(e.key)) {
        if (!isInputFocused || scannerMode) {
          buffer += e.key;
        }
      }
    };
    
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [scannerMode, isDialogOpen, products]);

  const addItem = () => {
    let productName = "";
    let productId = null;
    
    if (inputMode === "select") {
      if (!selectedProduct || !itemQuantity || !itemPrice) {
        toast.error(language === "ar" ? "أكمل بيانات المنتج" : "Complete product data");
        return;
      }
      const product = products.find(p => p.id === selectedProduct);
      productName = product.name;
      productId = product.id;
    } else {
      if (!manualProductName || !itemQuantity || !itemPrice) {
        toast.error(language === "ar" ? "أكمل بيانات المنتج" : "Complete product data");
        return;
      }
      productName = manualProductName;
    }
    
    const item = {
      product_id: productId,
      product_name: productName,
      quantity: parseInt(itemQuantity),
      price: parseFloat(itemPrice),
      total: parseInt(itemQuantity) * parseFloat(itemPrice),
    };
    setPurchaseItems([...purchaseItems, item]);
    setSelectedProduct("");
    setManualProductName("");
    setItemQuantity("");
    setItemPrice("");
  };

  const removeItem = (index) => {
    setPurchaseItems(purchaseItems.filter((_, i) => i !== index));
  };

  const purchaseTotal = purchaseItems.reduce((sum, item) => sum + item.total, 0);

  const handleSubmit = async () => {
    if (purchaseItems.length === 0) {
      toast.error(language === "ar" ? "أضف منتجات" : "Add products");
      return;
    }
    
    const supplierName = selectedSupplier 
      ? suppliers.find(s => s.id === selectedSupplier)?.name 
      : manualSupplierName;
    
    if (!supplierName) {
      toast.error(language === "ar" ? "أدخل اسم المورد" : "Enter supplier name");
      return;
    }

    try {
      const purchaseData = {
        items: purchaseItems,
        total: purchaseTotal,
        supplier_id: selectedSupplier || null,
        supplier_name: supplierName,
        payment_type: paymentType,
        amount_paid: paymentType === "cash" ? purchaseTotal : parseFloat(amountPaid) || 0,
        due_date: paymentType === "credit" && dueDate ? dueDate : null,
        category: purchaseCategory,
        notes: notes,
        exchange_rate: usdRate,  // freeze USD rate at invoice creation
        // ✨ تاريخ مخصص (للفواتير القديمة)
        invoice_date: useCustomDate && customInvoiceDate
          ? new Date(customInvoiceDate).toISOString()
          : null,
      };
      
      const response = await axios.post(`${API}/purchases`, purchaseData);
      const isOffline = response.data?._offline_pending === true;
      
      if (isOffline) {
        toast.success(language === "ar" ? "💾 تم الحفظ محلياً — سيُرسل عند رجوع الإنترنت" : "💾 Saved locally");
      } else {
        toast.success(language === "ar" ? "تم تسجيل المشتريات" : "Purchase recorded");
        // فتح الوصل (online only)
        setReceiptData({
          ...response.data,
          type: "purchase",
          supplier_name: supplierName,
        });
        setIsReceiptOpen(true);
      }
      
      fetchData();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    }
  };

  const openReceipt = (purchase) => {
    setReceiptData({
      ...purchase,
      type: "purchase",
    });
    setIsReceiptOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setPurchaseItems([]);
    setSelectedSupplier("");
    setManualSupplierName("");
    setPaymentType("cash");
    setAmountPaid("");
    setDueDate("");
    setPurchaseCategory("goods");
    setNotes("");
    setInputMode("select");
    setUseCustomDate(false);
    setCustomInvoiceDate("");
  };

  const getCategoryLabel = (value) => {
    const cat = purchaseCategories.find(c => c.value === value);
    return cat ? (language === "ar" ? cat.labelAr : cat.labelEn) : value;
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="purchases-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-heading font-bold text-white">{t("purchases")}</h1>
        <Button
          onClick={() => setIsDialogOpen(true)}
          className="bg-red-600 hover:bg-red-700 btn-lift"
          data-testid="new-purchase-btn"
        >
          <Plus className="w-4 h-4 me-2" />
          {t("newPurchase")}
        </Button>
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-red-600/30 border-t-red-600 rounded-full animate-spin" />
            </div>
          ) : purchases.length === 0 ? (
            <div className="text-center py-12">
              <Truck className="w-16 h-16 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">{t("noData")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header border-slate-700">
                    <TableHead className="text-amber-500">{language === "ar" ? "رقم الوصل" : "Invoice #"}</TableHead>
                    <TableHead className="text-amber-500">{t("date")}</TableHead>
                    <TableHead className="text-amber-500">{t("supplier")}</TableHead>
                    <TableHead className="text-amber-500">{language === "ar" ? "الفئة" : "Category"}</TableHead>
                    <TableHead className="text-amber-500">{language === "ar" ? "المنتجات" : "Items"}</TableHead>
                    <TableHead className="text-amber-500">{t("total")}</TableHead>
                    <TableHead className="text-amber-500">{t("paymentType")}</TableHead>
                    <TableHead className="text-amber-500">{language === "ar" ? "الوصل" : "Receipt"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchases.map((purchase) => (
                    <TableRow key={purchase.id} className="border-slate-700 hover:bg-slate-800/50">
                      <TableCell className="text-red-400 font-mono font-bold">
                        <div className="flex items-center gap-1">
                          <span>{purchase.invoice_number || "-"}</span>
                          {(purchase.corrections_count || 0) > 0 && (
                            <span
                              title={language === "ar" ? `${purchase.corrections_count} إشعار تصحيح` : `${purchase.corrections_count} correction(s)`}
                              className="text-amber-400 text-xs bg-amber-900/30 px-1 rounded"
                            >
                              📝{purchase.corrections_count}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">
                        <div className="flex items-center gap-2">
                          <span>{formatDateTime(purchase.invoice_date || purchase.created_at, language === "ar" ? "ar-IQ" : "en-US")}</span>
                          {purchase.is_backdated && (
                            <span title={language === "ar" ? "تاريخ مُعدَّل يدوياً" : "Manually backdated"} className="text-amber-400 text-xs">📅</span>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              setEditDatePurchase(purchase);
                              const d = new Date(purchase.invoice_date || purchase.created_at);
                              const offset = d.getTimezoneOffset();
                              const local = new Date(d.getTime() - offset * 60000);
                              setEditDateValue(local.toISOString().slice(0, 16));
                            }}
                            className="h-6 w-6 text-slate-500 hover:text-emerald-400"
                            title={language === "ar" ? "تعديل التاريخ" : "Edit date"}
                            data-testid={`edit-purchase-date-btn-${purchase.id}`}
                          >
                            ✏️
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-white font-medium">{purchase.supplier_name}</TableCell>
                      <TableCell className="text-slate-300">
                        {getCategoryLabel(purchase.category || "goods")}
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {purchase.items?.length || 0} {language === "ar" ? "منتج" : "items"}
                      </TableCell>
                      <TableCell className="text-red-500 font-bold">
                        {formatCurrency(purchase.total)}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          purchase.payment_type === "cash"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-amber-500/20 text-amber-400"
                        }`}>
                          {purchase.payment_type === "cash" ? t("cash") : t("credit")}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openReceipt(purchase)}
                            className="text-slate-400 hover:text-amber-500"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              setEditMetaPurchase(purchase);
                              setEditMetaForm({
                                supplier_name: purchase.supplier_name || "",
                                notes: purchase.notes || "",
                                due_date: purchase.due_date || "",
                              });
                            }}
                            className="h-8 w-8 text-slate-400 hover:text-emerald-400"
                            title={language === "ar" ? "تعديل بيانات الفاتورة" : "Edit invoice info"}
                            data-testid={`edit-purchase-meta-btn-${purchase.id}`}
                          >
                            ✏️
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              setCorrectionPurchase(purchase);
                              setCorrectionForm({ direction: "decrease", amount: "", reason: "", adjust_debt: true });
                            }}
                            className="h-8 w-8 text-slate-400 hover:text-amber-400"
                            title={language === "ar" ? "إصدار إشعار تصحيح (مدين)" : "Issue debit note"}
                            data-testid={`correction-purchase-btn-${purchase.id}`}
                          >
                            📝
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Purchase Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{t("newPurchase")}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* اختيار طريقة الإدخال */}
            <Tabs value={inputMode} onValueChange={setInputMode} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-slate-900">
                <TabsTrigger value="select" className="data-[state=active]:bg-red-600">
                  <Package className="w-4 h-4 me-2" />
                  {language === "ar" ? "من المخزون" : "From Inventory"}
                </TabsTrigger>
                <TabsTrigger value="manual" className="data-[state=active]:bg-red-600">
                  <Edit2 className="w-4 h-4 me-2" />
                  {language === "ar" ? "إدخال يدوي" : "Manual Entry"}
                </TabsTrigger>
              </TabsList>

              {/* اختيار من المخزون */}
              <TabsContent value="select" className="mt-4">
                <div className="space-y-3">
                  {/* أزرار مسح الباركود */}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => setIsScannerOpen(true)}
                      className="flex-1 bg-purple-600 hover:bg-purple-700"
                    >
                      <Camera className="w-5 h-5 me-2" />
                      {language === "ar" ? "مسح بالكاميرا" : "Camera Scan"}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setScannerMode(!scannerMode)}
                      className={`flex-1 transition-all ${
                        scannerMode 
                          ? "bg-emerald-500 hover:bg-emerald-600 ring-2 ring-emerald-400 animate-pulse" 
                          : "bg-slate-700 hover:bg-slate-600"
                      }`}
                    >
                      <ScanLine className="w-5 h-5 me-2" />
                      {scannerMode 
                        ? (language === "ar" ? "🟢 جاهز USB" : "🟢 USB Ready")
                        : (language === "ar" ? "وضع USB" : "USB Mode")
                      }
                    </Button>
                  </div>
                  
                  {/* تلميح وضع المسح */}
                  {scannerMode && (
                    <div className="p-2 rounded-lg bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 text-sm text-center">
                      🟢 {language === "ar" ? "وضع المسح USB نشط - امسح الباركود الآن!" : "USB scan mode active - scan barcode now!"}
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <Select 
                        value={selectedProduct} 
                        onValueChange={(productId) => {
                          setSelectedProduct(productId);
                          // تعبئة تلقائية لبيانات المنتج
                          const product = products.find(p => p.id === productId);
                          if (product) {
                            setItemPrice(product.purchase_price || product.cost_price || "");
                          }
                        }}
                      >
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white col-span-2">
                          <SelectValue placeholder={language === "ar" ? "اختر منتج" : "Select product"} />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                          {products.map((product) => (
                            <SelectItem key={product.id} value={product.id} className="text-white hover:bg-slate-700">
                              {product.name} {product.purchase_price ? `(${formatCurrency(product.purchase_price)})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        placeholder={t("quantity")}
                        value={itemQuantity}
                        onChange={(e) => setItemQuantity(e.target.value)}
                        className="bg-slate-900 border-slate-700 text-white"
                      />
                    </div>
                    <PriceInput
                      value={parseFloat(itemPrice) || 0}
                      onChange={(v) => setItemPrice(String(v))}
                      placeholder={t("purchasePrice")}
                      testId="item-price-input"
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                  
                  {/* عرض تفاصيل المنتج المختار */}
                  {selectedProduct && (() => {
                    const product = products.find(p => p.id === selectedProduct);
                    if (product) {
                      return (
                        <div className="bg-slate-800/50 rounded-lg p-3 border border-emerald-500/30">
                          <div className="grid grid-cols-3 gap-2 text-sm">
                            <div>
                              <span className="text-slate-400">{language === "ar" ? "سعر الشراء السابق:" : "Previous price:"}</span>
                              <span className="text-emerald-400 font-bold ms-2">{formatCurrency(product.purchase_price || 0)}</span>
                            </div>
                            <div>
                              <span className="text-slate-400">{language === "ar" ? "سعر البيع:" : "Sale price:"}</span>
                              <span className="text-amber-400 font-bold ms-2">{formatCurrency(product.sale_price || 0)}</span>
                            </div>
                            <div>
                              <span className="text-slate-400">{language === "ar" ? "المخزون:" : "Stock:"}</span>
                              <span className="text-white font-bold ms-2">{product.quantity || 0}</span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </TabsContent>

              {/* إدخال يدوي */}
              <TabsContent value="manual" className="mt-4">
                <div className="grid grid-cols-4 gap-2">
                  <Input
                    placeholder={language === "ar" ? "اسم المنتج" : "Product name"}
                    value={manualProductName}
                    onChange={(e) => setManualProductName(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white col-span-2"
                  />
                  <Input
                    type="number"
                    placeholder={t("quantity")}
                    value={itemQuantity}
                    onChange={(e) => setItemQuantity(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                  <Input
                    type="number"
                    placeholder={t("purchasePrice")}
                    value={itemPrice}
                    onChange={(e) => setItemPrice(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
              </TabsContent>
            </Tabs>

            <Button onClick={addItem} variant="outline" className="w-full border-slate-600 text-slate-300">
              <Plus className="w-4 h-4 me-2" />
              {language === "ar" ? "إضافة منتج" : "Add Item"}
            </Button>

            {/* Items List */}
            {purchaseItems.length > 0 && (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {purchaseItems.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 rounded bg-slate-900/50">
                    <span className="text-white">{item.product_name}</span>
                    <span className="text-slate-400">{item.quantity} × {formatCurrency(item.price)}</span>
                    <span className="text-green-500">{formatCurrency(item.total)}</span>
                    <Button size="icon" variant="ghost" onClick={() => removeItem(index)} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Total */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-900 border border-amber-500/30">
              <span className="text-amber-400">{t("total")}</span>
              <div className="flex flex-col items-end">
                <span className="text-white font-bold text-xl">{formatCurrency(purchaseTotal)}</span>
                <USDHint amount={purchaseTotal} />
              </div>
            </div>

            {/* فئة المشتريات */}
            <div className="space-y-2">
              <Label className="text-slate-200">{language === "ar" ? "فئة المشتريات" : "Purchase Category"}</Label>
              <Select value={purchaseCategory} onValueChange={(v) => { setPurchaseCategory(v); if (v !== "other") setCustomCategory(""); }}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {purchaseCategories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-slate-700">
                      {language === "ar" ? cat.labelAr : cat.labelEn}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* حقل كتابة الفئة المخصصة */}
              {purchaseCategory === "other" && (
                <Input
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  placeholder={language === "ar" ? "اكتب الفئة هنا..." : "Type category here..."}
                  className="bg-slate-900 border-slate-700 text-white mt-2"
                />
              )}
            </div>

            {/* Supplier Selection - with manual option */}
            <div className="space-y-2">
              <Label className="text-slate-200">{t("supplier")}</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select value={selectedSupplier} onValueChange={(v) => { setSelectedSupplier(v); setManualSupplierName(""); }}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                    <SelectValue placeholder={language === "ar" ? "اختر المورد" : "Select supplier"} />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id} className="text-white hover:bg-slate-700">
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder={language === "ar" ? "أو اكتب اسم جديد" : "Or enter new name"}
                  value={manualSupplierName}
                  onChange={(e) => { setManualSupplierName(e.target.value); setSelectedSupplier(""); }}
                  className="bg-slate-900 border-slate-700 text-white"
                />
              </div>
            </div>

            {/* Payment Type */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={paymentType === "cash" ? "default" : "outline"}
                onClick={() => setPaymentType("cash")}
                className={paymentType === "cash" ? "bg-green-600" : "border-slate-600 text-slate-300"}
              >
                {t("cash")}
              </Button>
              <Button
                type="button"
                variant={paymentType === "credit" ? "default" : "outline"}
                onClick={() => setPaymentType("credit")}
                className={paymentType === "credit" ? "bg-amber-600" : "border-slate-600 text-slate-300"}
              >
                {t("credit")}
              </Button>
            </div>

            {paymentType === "credit" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-200">{t("amountPaid")}</Label>
                  <PriceInput
                    value={parseFloat(amountPaid) || 0}
                    onChange={(v) => setAmountPaid(String(v))}
                    placeholder="0"
                    testId="purchase-amount-paid-input"
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-200">{language === "ar" ? "تاريخ الاستحقاق" : "Due Date"}</Label>
                  <Input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
              </div>
            )}

            {/* ملاحظات */}
            <div className="space-y-2">
              <Label className="text-slate-200">{language === "ar" ? "ملاحظات" : "Notes"}</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder={language === "ar" ? "ملاحظات إضافية..." : "Additional notes..."}
              />
            </div>

            {/* ✨ تاريخ مخصص للفاتورة */}
            <div className="space-y-2 border border-slate-700 rounded-lg p-3 bg-slate-900/40">
              <div className="flex items-center justify-between">
                <Label className="text-slate-200 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useCustomDate}
                    onChange={(e) => {
                      setUseCustomDate(e.target.checked);
                      if (e.target.checked && !customInvoiceDate) {
                        const now = new Date();
                        const offset = now.getTimezoneOffset();
                        const local = new Date(now.getTime() - offset * 60000);
                        setCustomInvoiceDate(local.toISOString().slice(0, 16));
                      }
                    }}
                    className="w-4 h-4 accent-emerald-500"
                    data-testid="use-custom-purchase-date-checkbox"
                  />
                  📅 {language === "ar" ? "تحديد تاريخ مخصص للفاتورة" : "Set custom invoice date"}
                </Label>
              </div>
              {useCustomDate && (
                <div className="space-y-2">
                  <input
                    type="datetime-local"
                    value={customInvoiceDate}
                    onChange={(e) => setCustomInvoiceDate(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                    data-testid="custom-purchase-date-input"
                  />
                  <p className="text-xs text-amber-400/80">
                    ⚠️ {language === "ar"
                      ? "سيُسجَّل التاريخ الحقيقي للإنشاء أيضاً (للتدقيق)"
                      : "Actual creation time will be logged (for audit)"}
                  </p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={handleCloseDialog} className="border-slate-600 text-slate-300">
              {t("cancel")}
            </Button>
            <Button type="button" onClick={handleSubmit} className="bg-red-600 hover:bg-red-700">
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      {receiptData && (
        <ReceiptDialog
          isOpen={isReceiptOpen}
          onClose={() => setIsReceiptOpen(false)}
          data={receiptData}
          type="purchase"
        />
      )}

      {/* ماسح الباركود للمشتريات */}
      <BarcodeScanner
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onScan={handlePurchaseBarcodeScan}
        language={language}
      />

      {/* ✨ Dialog: تعديل تاريخ فاتورة مشتريات */}
      <Dialog open={!!editDatePurchase} onOpenChange={(open) => { if (!open) { setEditDatePurchase(null); setEditDateValue(""); } }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-400">
              📅 {language === "ar" ? "تعديل تاريخ الفاتورة" : "Edit Invoice Date"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-sm text-slate-400">
              {language === "ar" ? "رقم الوصل: " : "Invoice #: "}
              <span className="text-red-400 font-mono font-bold">{editDatePurchase?.invoice_number}</span>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">
                {language === "ar" ? "التاريخ والوقت الجديد" : "New date & time"}
              </Label>
              <input
                type="datetime-local"
                value={editDateValue}
                onChange={(e) => setEditDateValue(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                data-testid="edit-purchase-invoice-date-input"
              />
            </div>
            <p className="text-xs text-amber-400/80">
              ⚠️ {language === "ar"
                ? "سيتم حفظ تاريخ التعديل للتدقيق الداخلي"
                : "Edit timestamp will be logged for audit"}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setEditDatePurchase(null); setEditDateValue(""); }} className="border-slate-600 text-slate-300">
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                if (!editDateValue) {
                  toast.error(language === "ar" ? "اختر التاريخ" : "Choose a date");
                  return;
                }
                try {
                  const iso = new Date(editDateValue).toISOString();
                  await axios.patch(`${API}/purchases/${editDatePurchase.id}/date`, { invoice_date: iso });
                  toast.success(language === "ar" ? "✅ تم تعديل تاريخ الفاتورة" : "Invoice date updated");
                  setEditDatePurchase(null);
                  setEditDateValue("");
                  fetchData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || t("error"));
                }
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-purchase-invoice-date-btn"
            >
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ✨ Dialog: تعديل بيانات الفاتورة (آمن - مستوى 1) */}
      <Dialog open={!!editMetaPurchase} onOpenChange={(open) => { if (!open) setEditMetaPurchase(null); }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-emerald-400">
              ✏️ {language === "ar" ? "تعديل بيانات الفاتورة" : "Edit Invoice Info"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm text-slate-400">
              {language === "ar" ? "رقم الوصل: " : "Invoice #: "}
              <span className="text-red-400 font-mono font-bold">{editMetaPurchase?.invoice_number}</span>
            </div>
            <div className="bg-slate-900/40 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-400">
              💡 {language === "ar"
                ? "هذا التعديل آمن: لا يلمس المنتجات أو الأموال أو المخزون أو الديون"
                : "Safe edit: items, amounts, stock, and debts remain unchanged"}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">{language === "ar" ? "اسم المورد" : "Supplier name"}</Label>
              <Input
                value={editMetaForm.supplier_name}
                onChange={(e) => setEditMetaForm({ ...editMetaForm, supplier_name: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="edit-purchase-meta-supplier"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">{language === "ar" ? "ملاحظات" : "Notes"}</Label>
              <textarea
                value={editMetaForm.notes}
                onChange={(e) => setEditMetaForm({ ...editMetaForm, notes: e.target.value })}
                rows={3}
                className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 resize-none"
                data-testid="edit-purchase-meta-notes"
              />
            </div>
            {editMetaPurchase?.payment_type === "credit" && (
              <div className="space-y-2">
                <Label className="text-slate-200">{language === "ar" ? "تاريخ الاستحقاق" : "Due date"}</Label>
                <Input
                  type="date"
                  value={editMetaForm.due_date ? editMetaForm.due_date.slice(0, 10) : ""}
                  onChange={(e) => setEditMetaForm({ ...editMetaForm, due_date: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="edit-purchase-meta-due-date"
                />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditMetaPurchase(null)} className="border-slate-600 text-slate-300">
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                try {
                  await axios.patch(`${API}/purchases/${editMetaPurchase.id}/meta`, {
                    supplier_name: editMetaForm.supplier_name,
                    notes: editMetaForm.notes,
                    due_date: editMetaForm.due_date || null,
                  });
                  toast.success(language === "ar" ? "✅ تم تعديل بيانات الفاتورة" : "Invoice info updated");
                  setEditMetaPurchase(null);
                  fetchData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || t("error"));
                }
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-purchase-meta-btn"
            >
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ✨ Dialog: إصدار إشعار تصحيح (Debit Note) */}
      <Dialog open={!!correctionPurchase} onOpenChange={(open) => { if (!open) setCorrectionPurchase(null); }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-amber-400">
              📝 {language === "ar" ? "إصدار إشعار تصحيح (مدين)" : "Issue Debit Note"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-slate-900/40 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-400">
              💡 {language === "ar"
                ? "بدلاً من تعديل الفاتورة الأصلية، يُصدر إشعار رسمي يوضح التصحيح."
                : "Instead of editing the original invoice, an official correction note is issued."}
            </div>
            <div className="text-sm text-slate-400">
              {language === "ar" ? "الفاتورة الأصلية: " : "Original invoice: "}
              <span className="text-red-400 font-mono font-bold">{correctionPurchase?.invoice_number}</span>
              {" — "}
              <span className="text-red-400">{formatCurrency(correctionPurchase?.total || 0)}</span>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200">{language === "ar" ? "نوع التصحيح" : "Correction type"}</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={correctionForm.direction === "decrease" ? "default" : "outline"}
                  onClick={() => setCorrectionForm({ ...correctionForm, direction: "decrease" })}
                  className={correctionForm.direction === "decrease" ? "bg-rose-600 hover:bg-rose-700" : "border-slate-600 text-slate-300"}
                >
                  ↓ {language === "ar" ? "تخفيض المبلغ" : "Decrease"}
                </Button>
                <Button
                  type="button"
                  variant={correctionForm.direction === "increase" ? "default" : "outline"}
                  onClick={() => setCorrectionForm({ ...correctionForm, direction: "increase" })}
                  className={correctionForm.direction === "increase" ? "bg-emerald-600 hover:bg-emerald-700" : "border-slate-600 text-slate-300"}
                >
                  ↑ {language === "ar" ? "زيادة المبلغ" : "Increase"}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200">{language === "ar" ? "المبلغ" : "Amount"}</Label>
              <Input
                type="number"
                value={correctionForm.amount}
                onChange={(e) => setCorrectionForm({ ...correctionForm, amount: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder="0"
                data-testid="correction-purchase-amount"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200">{language === "ar" ? "السبب (إلزامي)" : "Reason (required)"}</Label>
              <textarea
                value={correctionForm.reason}
                onChange={(e) => setCorrectionForm({ ...correctionForm, reason: e.target.value })}
                rows={3}
                placeholder={language === "ar" ? "مثال: خصم لاحق من المورد، إرجاع بضاعة..." : "e.g. supplier discount, return..."}
                className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40 resize-none"
                data-testid="correction-purchase-reason"
              />
            </div>

            {correctionPurchase?.payment_type === "credit" && correctionPurchase?.supplier_id && (
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={correctionForm.adjust_debt}
                  onChange={(e) => setCorrectionForm({ ...correctionForm, adjust_debt: e.target.checked })}
                  className="w-4 h-4 accent-amber-500"
                />
                {language === "ar" ? "تعديل دين المورد تلقائياً" : "Auto-adjust supplier debt"}
              </label>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCorrectionPurchase(null)} className="border-slate-600 text-slate-300">
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                const amount = parseFloat(correctionForm.amount);
                if (!amount || amount <= 0) {
                  toast.error(language === "ar" ? "أدخل مبلغ صحيح" : "Enter a valid amount");
                  return;
                }
                if (!correctionForm.reason.trim()) {
                  toast.error(language === "ar" ? "أدخل سبب التصحيح" : "Enter reason");
                  return;
                }
                try {
                  const r = await axios.post(`${API}/correction-notes`, {
                    invoice_type: "purchase",
                    invoice_id: correctionPurchase.id,
                    direction: correctionForm.direction,
                    amount,
                    reason: correctionForm.reason.trim(),
                    adjust_debt: correctionForm.adjust_debt,
                  });
                  toast.success(language === "ar" ? `✅ تم إصدار الإشعار ${r.data.note_number}` : `Issued ${r.data.note_number}`);
                  setCorrectionPurchase(null);
                  fetchData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || t("error"));
                }
              }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="save-purchase-correction-btn"
            >
              {language === "ar" ? "إصدار الإشعار" : "Issue Note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
